import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import ConfirmDialog from '../common/ConfirmDialog.js';
import SearchField from '../common/SearchField.js';
import LowStockItem from './LowStockItem.js';
import RefreshButton from '../common/RefreshButton.js';
import Pagination from '../common/Pagination.js';
import OrderPrintContent from '../common/OrderPrintContent.js';
import { useOrderStore } from '../../store/branchstore/branchOrdersStore.js';
import { cancelOrder } from '../../services/orderApi.js';
import { getWaiters } from '../../services/waiterApi.js';
import useAppStore from '../../store/useAppStore.js';
import { getSessionDate, isWithinSession } from '../../utils/dateHelpers.js';

const Dashboard = () => {
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, orderId: null, action: null });
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [stockSearchQuery, setStockSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPrintOrder, setCurrentPrintOrder] = useState(null);
  const [isPrintReady, setIsPrintReady] = useState(false);
  const [waiters, setWaiters] = useState([]);
  const [waitersError, setWaitersError] = useState('');
  const printRef = useRef(null);

  const {loadDashboardData, setLowStockItems, branchTotals, isLoading, error, refreshDashboardData } = useAppStore((state) => ({
    loadDashboardData: state.loadDashboardData,
    setLowStockItems: state.setLowStockItems,
    branchTotals: state.branchTotals,
    isLoading: state.isDashboardLoading,
    error: state.dashboardError,
    refreshDashboardData: state.refreshDashboardData,
  }));

  const lowStockItems = useAppStore((state) => state.lowStockItems);

  const { orders, loading: ordersLoading, initializeOrders, refreshOrders, updateOrderStatus } = useOrderStore((state) => ({
    orders: state.orders,
    loading: state.loading,
    initializeOrders: state.initializeOrders,
    refreshOrders: state.refreshOrders,
    updateOrderStatus: state.updateOrderStatus,
  }));

  useEffect(() => {
    const loadData = async () => {
      try {
        await loadDashboardData();
      } catch (err) {
        console.error('Error loading dashboard data:', err);
      }
    };

    loadData();
  }, [loadDashboardData]);

  useEffect(() => {
    const loadOrdersData = async () => {
      try {
        await initializeOrders();
      } catch (err) {
        console.error('Error loading orders:', err);
      }
    };

    loadOrdersData();
  }, [initializeOrders]);

  const todaysOrders = useMemo(() => {
    const sessionDate = getSessionDate(new Date());
    return orders.filter((order) => {
      if (order.business_date) {
        return order.business_date === sessionDate;
      }
      if (!order.created_at) return false;
      return isWithinSession(order.created_at);
    });
  }, [orders]);

  const todaySales = useMemo(() => {
    return todaysOrders.reduce((sum, order) => {
      if (order.status === 'cancelled') return sum;
      return sum + (Number(order.total) || 0);
    }, 0);
  }, [todaysOrders]);

  const filteredOrders = useMemo(() => {
    return todaysOrders.filter((order) => {
      const searchTerm = orderSearchQuery.toLowerCase();
      const matchesSearch = !searchTerm ||
        String(order.id).toLowerCase().includes(searchTerm) ||
        String(order.token_number || order.token?.token_no || '').toLowerCase().includes(searchTerm) ||
        String(order.table_name || '').toLowerCase().includes(searchTerm) ||
        String(order.cashier_name || '').toLowerCase().includes(searchTerm);

      const matchesType = !orderTypeFilter || order.order_type === orderTypeFilter;
      const matchesStatus = !orderStatusFilter || order.status === orderStatusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [todaysOrders, orderSearchQuery, orderTypeFilter, orderStatusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [orderSearchQuery, orderTypeFilter, orderStatusFilter]);

  const filteredStockItems = lowStockItems.filter((item) =>
    item.name.toLowerCase().includes(stockSearchQuery.toLowerCase()) ||
    item.sku.toLowerCase().includes(stockSearchQuery.toLowerCase())
  );

  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * 10;
    return filteredOrders.slice(startIndex, startIndex + 10);
  }, [filteredOrders, currentPage]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / 10));

  const getWaiterName = (waiterId) => {
    if (!waiterId) return 'N/A';
    const waiter = waiters.find((w) => w.id === waiterId);
    return waiter ? waiter.name : `Waiter #${waiterId}`;
  };

  const getOrderTypeDisplay = (orderType, tableName, waiterId) => {
    switch (orderType) {
      case 'walkin':
        return 'Walk-in';
      case 'table':
        return `Table: ${tableName || 'N/A'}`;
      case 'waiter':
        return `Waiter: ${getWaiterName(waiterId)}`;
      default:
        return orderType || 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'confirmed':
        return 'bg-blue-100 text-blue-700';
      case 'ready':
        return 'bg-purple-100 text-purple-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatAmount = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00';
  };

  const handleCancelOrder = (orderId) => {
    setConfirmDialog({ isOpen: true, orderId, action: 'cancel' });
  };

  const handleConfirmCancel = async () => {
    const { orderId } = confirmDialog;
    try {
      await cancelOrder(orderId);
      updateOrderStatus(orderId, { status: 'cancelled' });
    } catch (err) {
      console.error('Error cancelling order:', err);
    } finally {
      setConfirmDialog({ isOpen: false, orderId: null, action: null });
    }
  };

  const handleCloseDialog = () => {
    setConfirmDialog({ isOpen: false, orderId: null, action: null });
  };

  const handlePrintOrder = (order) => {
    setCurrentPrintOrder(order);
    setIsPrintReady(false);
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Receipt-${new Date().getTime()}`,
    pageStyle: `
      @page {
        size: 80mm auto;
        margin: 0;
      }
      body {
        margin: 0;
        padding: 0;
      }
    `,
    onAfterPrint: () => {
      setCurrentPrintOrder(null);
      setIsPrintReady(false);
    }
  });

  useEffect(() => {
    const loadWaiters = async () => {
      try {
        const response = await getWaiters();
        setWaiters(response.waiters || []);
        setWaitersError('');
      } catch (err) {
        console.error('Error loading waiters:', err);
        setWaitersError('Failed to load waiter data');
      }
    };

    loadWaiters();
  }, []);

  useEffect(() => {
    if (currentPrintOrder && isPrintReady) {
      const timer = setTimeout(() => {
        handlePrint();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentPrintOrder, isPrintReady, handlePrint]);

  const isRefreshing = isLoading || ordersLoading;

  const handleRefresh = async () => {
    try {
      await Promise.all([refreshDashboardData(), refreshOrders()]);
    } catch (err) {
      console.error('Error refreshing dashboard:', err);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
          <p className="text-gray-700">
            Welcome to the Branch Manager Dashboard. Here you can overview key metrics and activities.
          </p>
        </div>
        <div className="flex gap-3">
          <RefreshButton onClick={handleRefresh} loading={isRefreshing} text={false} />
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading dashboard...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {!isLoading && !error && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Today's Sales</h3>
              <p className="text-3xl font-bold text-blue-600">{formatAmount(branchTotals.today)}</p>
              <p className="text-sm text-gray-500 mt-2">Sales by business_date for today</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Today Orders</h3>
              <p className="text-3xl font-bold text-purple-600">{todaysOrders.length}</p>
              <p className="text-sm text-gray-500 mt-2">Total orders for today</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Low Stock Items</h3>
              <p className="text-3xl font-bold text-yellow-600">{lowStockItems.length}</p>
              <p className="text-sm text-gray-500 mt-2">Items below reorder</p>
            </div>
          </div>

          {/* Today's Orders Table */}
          <div className="mb-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold">Today's Orders</h2>
                <p className="text-gray-500 text-sm">Same order view as the Orders tab, filtered for today.</p>
              </div>
              <div className="grid gap-3 w-full md:w-auto md:grid-cols-3">
                <SearchField
                  value={orderSearchQuery}
                  onChange={setOrderSearchQuery}
                  placeholder="Search by order, token, table or cashier..."
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Order Type</label>
                  <select
                    value={orderTypeFilter}
                    onChange={(e) => setOrderTypeFilter(e.target.value)}
                    className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="">All Types</option>
                    <option value="walkin">Walk-in</option>
                    <option value="table">Table</option>
                    <option value="waiter">Waiter</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={orderStatusFilter}
                    onChange={(e) => setOrderStatusFilter(e.target.value)}
                    className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>  
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-auto h-[400px]">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Order ID</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-700">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Cashier</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Created</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {ordersLoading ? (
                      <tr>
                        <td colSpan="7" className="p-8 text-center text-gray-500">
                          Loading today's orders...
                        </td>
                      </tr>
                    ) : filteredOrders.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="p-8 text-center text-gray-500">
                          {orderSearchQuery || orderTypeFilter || orderStatusFilter ? 'No orders match your filters' : 'No orders found for today'}
                        </td>
                      </tr>
                    ) : (
                      paginatedOrders.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-semibold text-gray-900">ORD-{order.order_number || order.id}</span>
                            {(order.token_number || order.token?.token_no) && (
                              <div className="text-xs text-gray-500">Token: {order.token_number || order.token?.token_no}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                            {getOrderTypeDisplay(order.order_type, order.table_name, order.waiter_id)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold capitalize ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-gray-900">
                            {formatAmount(order.total)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                            {order.cashier_name || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">{order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}</div>
                            <div className="text-xs text-gray-500">{order.created_at ? new Date(order.created_at).toLocaleTimeString() : '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex gap-2 justify-center">
                              <button
                                type="button"
                                onClick={() => handlePrintOrder(order)}
                                className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-300 hover:bg-gray-50 transition text-gray-600 hover:text-gray-900"
                                title="Print Receipt"
                              >
                                <span className="text-sm">🖨️</span>
                              </button>
                              {order.status !== 'cancelled' && (
                                <button
                                  type="button"
                                  onClick={() => handleCancelOrder(order.id)}
                                  className="flex items-center justify-center w-8 h-8 rounded-lg border border-red-300 hover:bg-red-50 transition text-red-600 hover:text-red-900"
                                  title="Cancel Order"
                                >
                                  <span className="text-sm">✕</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {filteredOrders.length > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <Pagination
                    currentPage={currentPage}
                    totalItems={filteredOrders.length}
                    pageSize={10}
                    onPageChange={setCurrentPage}
                    className="bg-white"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Low Stock Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Low Stock Alert</h2>
              <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{filteredStockItems.length} items</span>
            </div>
            
            {/* Search Field */}
            <div className="mb-4">
              <SearchField
                value={stockSearchQuery}
                onChange={setStockSearchQuery}
                placeholder="Search by product name or SKU..."
              />
            </div>

            {/* Horizontal Scrollable Container */}
            {filteredStockItems.length === 0 ? (
              <div className="bg-white rounded-lg p-8 text-center text-gray-500 border border-gray-200">
                {stockSearchQuery ? 'No items match your search' : 'All items in stock'}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto" style={{ maxWidth: '100vw', marginLeft: 'calc(-50vw + 50%)', paddingLeft: 'calc(50vw - 50%)' }}>
                <div className="flex gap-4 p-4" style={{ width: 'fit-content' }}>
                  {filteredStockItems.map((item) => (
                    <div key={item.id} style={{ minWidth: '300px', flexShrink: 0 }}>
                      <LowStockItem item={item} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmCancel}
        title="Cancel Order"
        message="Are you sure you want to cancel this order?"
      />
    </div>
  );
};

export default Dashboard;